import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, controlMatrixApi, peopleApi } from '../services/api';
import type { Person } from '../types/models';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { RaiseArtefactModal } from '../components/RaiseArtefactModal';
import { toneFromMap, type Tone } from '../constants/tone';

type Control = {
  controlId?: string;
  fields?: Record<string, any>;
};

type SortCol = 'id' | 'title' | 'framework' | 'domain' | 'status' | 'owner';

const get = (c: Control, fieldId: string): string => {
  const v = c.fields?.[fieldId];
  if (v === undefined || v === null) return '';
  return String(v);
};

const CONTROL_STATUS_TONE: Record<string, Tone> = { active: 'success', draft: 'neutral', retired: 'danger' };

export const ControlsList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const matrixId = searchParams.get('matrixId') || '';

  const [frameworkFilter, setFrameworkFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [raiseControl, setRaiseControl] = useState<{ id: string; title: string } | null>(null);

  const { data: matrix } = useQuery<any>({
    queryKey: ['controlMatrix', matrixId],
    queryFn: () => controlMatrixApi.getOne(matrixId),
    enabled: !!matrixId,
  });

  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  const {
    data: controls = [],
    isLoading,
    error,
  } = useQuery<Control[]>({
    queryKey: ['controls', matrixId],
    queryFn: async () => {
      const res = await api.get<{ controls: Control[] }>(`/controls?matrixId=${encodeURIComponent(matrixId)}`);
      return res.controls || [];
    },
    enabled: !!matrixId,
  });

  const searched = useMemo(() => {
    let items = controls;

    if (frameworkFilter) items = items.filter((c) => get(c, 'framework') === frameworkFilter);
    if (statusFilter) items = items.filter((c) => get(c, 'status') === statusFilter);
    if (domainFilter) items = items.filter((c) => get(c, 'domain') === domainFilter);

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      items = items.filter((c) => {
        const title = get(c, 'control_title').toLowerCase();
        const cid = (get(c, 'control_id') || c.controlId || '').toLowerCase();
        const def = get(c, 'control_definition').toLowerCase();
        return title.includes(term) || cid.includes(term) || def.includes(term);
      });
    }

    return items;
  }, [controls, frameworkFilter, statusFilter, domainFilter, searchTerm]);

  // useSortableData's comparator already collates digit runs numerically (Intl `numeric: true`),
  // which gives the same '5.2' < '5.10' ordering the control-ID column needs.
  const { sorted: filtered, field: sortCol, dir: sortDir, toggle: handleSort } = useSortableData<Control, SortCol>(
    searched,
    (c, col) => {
      switch (col) {
        case 'id':        return get(c, 'control_id') || c.controlId || '';
        case 'title':     return get(c, 'control_title');
        case 'framework': return get(c, 'framework');
        case 'domain':    return get(c, 'domain');
        case 'status':    return get(c, 'status');
        case 'owner':     return get(c, 'control_owner');
      }
    },
    'id',
  );
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(filtered, 25);

  const frameworks = useMemo(() => {
    const list = matrix?.frameworks || matrix?.matrix?.frameworks;
    return Array.isArray(list) ? list : [];
  }, [matrix]);

  if (!matrixId) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-warning">
          <i className="ri-error-warning-line"></i>
          Missing <strong>matrixId</strong> query param. Open from Control Matrices.
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-list-check-2"
        title="Controls"
        subtitle={matrix?.name || matrix?.matrix?.name || undefined}
        colour="primary"
        actions={
          <>
            <Link className="np-btn np-btn-primary" to={`/controls/new?matrixId=${encodeURIComponent(matrixId)}`}>
              <i className="ri-add-line"></i>New Control
            </Link>
            <Link className="np-btn np-btn-ghost" to="/control-matrices">
              <i className="ri-arrow-left-line"></i>Matrices
            </Link>
          </>
        }
      />

      <div className="np-card">
        <div className="np-card-body">
          <div className="row g-3">
            <div className="col-12 col-md-3">
              <div className="np-field np-mb-0">
                <label>Framework</label>
                <select className="np-select" value={frameworkFilter} onChange={(e) => setFrameworkFilter(e.target.value)}>
                  <option value="">All</option>
                  {frameworks.map((fw: string) => (
                    <option key={fw} value={fw}>
                      {fw}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="np-field np-mb-0">
                <label>Status</label>
                <select className="np-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All</option>
                  <option value="active">active</option>
                  <option value="draft">draft</option>
                  <option value="retired">retired</option>
                </select>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="np-field np-mb-0">
                <label>Domain</label>
                <input className="np-input" value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)} placeholder="e.g. Security" />
              </div>
            </div>
            <div className="col-12 col-md-3">
              <div className="np-field np-mb-0">
                <label>Search</label>
                <input className="np-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search controls…" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="np-card">
        <div className="np-card-body">
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Failed to load controls.</div>
          ) : filtered.length === 0 ? (
            <div className="np-sub">No controls found</div>
          ) : (
            <NimbusTable thead={<tr>
                    <SortableTh field="id" active={sortCol === 'id'} dir={sortDir} onSort={handleSort} width="140px">Control ID</SortableTh>
                    <SortableTh field="title" active={sortCol === 'title'} dir={sortDir} onSort={handleSort}>Title</SortableTh>
                    <SortableTh field="framework" active={sortCol === 'framework'} dir={sortDir} onSort={handleSort} width="160px">Framework</SortableTh>
                    <SortableTh field="domain" active={sortCol === 'domain'} dir={sortDir} onSort={handleSort} width="160px">Domain</SortableTh>
                    <SortableTh field="status" active={sortCol === 'status'} dir={sortDir} onSort={handleSort} width="140px">Status</SortableTh>
                    <SortableTh field="owner" active={sortCol === 'owner'} dir={sortDir} onSort={handleSort} width="200px">Owner</SortableTh>
                    <th style={{ width: '120px' }}></th>
                  </tr>}>
                  {pageItems.map((c, idx) => {
                    const ownerId = get(c, 'control_owner');
                    const owner = people.find((p) => p.personId === ownerId);
                    const ownerName = owner
                      ? owner.personName
                      : ownerId?.match(/^PERSON-/i)
                        ? 'Unknown User'
                        : ownerId;
                    const idVal = get(c, 'control_id') || c.controlId || `row-${idx}`;
                    const title = get(c, 'control_title') || '-';
                    const framework = get(c, 'framework');
                    const domain = get(c, 'domain');
                    const status = get(c, 'status');

                    // Backend returns controlId for route; prefer that
                    const routeId = c.controlId || idVal;

                    return (
                      <tr key={String(routeId)}>
                        <td className="np-tabular" style={{ color: 'var(--bs-secondary-color)' }}>{idVal}</td>
                        <td className="np-row-title-static">{title}</td>
                        <td>{framework ? <span className="np-chip np-tone-neutral">{framework}</span> : <span className="np-sub">—</span>}</td>
                        <td>{domain ? <span className="np-chip np-tone-neutral">{domain}</span> : <span className="np-sub">—</span>}</td>
                        <td>{status ? <span className={`np-chip np-tone-${toneFromMap(CONTROL_STATUS_TONE, status, 'neutral')}`}>{status}</span> : <span className="np-sub">—</span>}</td>
                        <td>{ownerName || '-'}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Link
                              className="np-icon-btn"
                              to={`/controls/${encodeURIComponent(String(routeId))}?matrixId=${encodeURIComponent(matrixId)}`}
                              title="View"
                            >
                              <i className="ri-eye-line"></i>
                            </Link>
                            <Link
                              className="np-icon-btn"
                              to={`/controls/${encodeURIComponent(String(routeId))}?matrixId=${encodeURIComponent(matrixId)}`}
                              title="Edit"
                            >
                              <i className="ri-edit-line"></i>
                            </Link>
                            <button
                              className="np-icon-btn np-icon-btn-success"
                              title="Raise Issue / Incident / Remediation"
                              onClick={() => setRaiseControl({ id: String(routeId), title })}
                            >
                              <i className="ri-add-circle-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </NimbusTable>
          )}
          {pageItems.length > 0 && (
            <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
          )}
        </div>
      </div>
      {raiseControl && (
        <RaiseArtefactModal
          controlId={raiseControl.id}
          controlTitle={raiseControl.title}
          controlRegister={matrixId}
          onClose={() => setRaiseControl(null)}
        />
      )}
    </div>
  );
};
